import {useCallback, useEffect, useRef, useState} from 'react'
import {useNavigate} from 'react-router'
import {useQueryClient} from '@tanstack/react-query'
import {onUserNotification} from '@/services/reverb'
import {logoLatest} from '@img/index.js'

/**
 * RUNTIME NOTIFICATION TOASTS — the driver portal's banner, on the passenger side.
 *
 * Every `.notification.broadcast` on the passenger's own channel shows as a card: hop'n
 * tile, title, one-line message, ✕. Top RIGHT on a desktop, top CENTRE full width below
 * 768. Gone on its own after 5s; tapping it opens the ride it is about, or the
 * notifications page.
 *
 * ⚠️ IT DOES NOT OPEN THE SOCKET. `PickupUpdatedModal` owns the user-channel subscription
 * for the whole session; this only listens on it (`onUserNotification`). The flight-moved
 * pickup is skipped here because that modal already shows it.
 */

const SKIP_SLUGS = new Set(['passenger.flight-pickup-updated'])
const AUTO_HIDE_MS = 10000

let nextId = 1

function Toast({toast, onDismiss}) {
    const navigate = useNavigate()

    useEffect(() => {
        const timer = setTimeout(() => onDismiss(toast.id), AUTO_HIDE_MS)
        return () => clearTimeout(timer)
    }, [toast.id, onDismiss])

    const open = () => {
        navigate(toast.to)
        onDismiss(toast.id)
    }

    return (
        <div
            role="status"
            onClick={open}
            className="pointer-events-auto flex w-full cursor-pointer items-center gap-3 rounded-3xl bg-(--bg-white) p-3 pr-4 shadow-[0_10px_30px_rgba(0,0,0,0.15)]"
        >
            <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-(--accent-green-light) max-md:h-10 max-md:w-10 max-md:rounded-xl">
                <img src={logoLatest} alt="hop'n" className="w-9 max-md:w-8"/>
            </span>

            <div className="min-w-0 flex-1">
                <p className="mb-0! truncate font-semibold text-(--text-primary) max-md:text-sm">{toast.title}</p>
                {toast.body && <p className="mb-0! truncate text-sm text-(--text-secondary) max-md:text-xs">{toast.body}</p>}
            </div>

            <button
                type="button"
                onClick={(e) => { e.stopPropagation(); onDismiss(toast.id) }}
                aria-label="Dismiss"
                className="flex h-8 w-8 shrink-0 cursor-pointer items-center justify-center text-(--text-secondary) hover:text-(--text-primary)"
            >
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
                    <path d="M1 1l12 12M13 1L1 13" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
                </svg>
            </button>
        </div>
    )
}

export default function NotificationToasts() {
    const queryClient = useQueryClient()
    const [toasts, setToasts] = useState([])
    const seen = useRef(new Set())

    const dismiss = useCallback((id) => setToasts((list) => list.filter((t) => t.id !== id)), [])

    useEffect(() => onUserNotification((payload) => {
        // The bell and the list are stale whatever this is.
        queryClient.invalidateQueries({queryKey: ['notifications']})

        const slug = payload?.slug || payload?.data?.type
        if (SKIP_SLUGS.has(slug)) return

        /* The driver reporting the pickup point stamps the trip (`reached_pickup.driver_at`); refresh
           it so the "Driver has arrived" badge shows now rather than on the next poll. */
        if (slug === 'passenger.driver-reached-pickup') {
            const reachedTripId = payload?.trip_id ?? payload?.data?.trip_id ?? payload?.notification?.trip_id ?? null
            queryClient.invalidateQueries({queryKey: reachedTripId != null ? ['trip', reachedTripId] : ['trip']})
            queryClient.invalidateQueries({queryKey: ['trips']})
        }

        // The same notification arriving twice (two event spellings, a reconnect) shows once.
        const key = payload?.id ?? payload?.data?.id
        if (key != null) {
            if (seen.current.has(String(key))) return
            seen.current.add(String(key))
        }

        const n = [payload?.notification, payload?.data, payload]
            .find((c) => c && typeof c === 'object' && (c.title || c.message || c.body)) || {}
        const tripId = n.trip_id ?? n.action?.trip_id ?? payload?.trip_id ?? null

        setToasts((list) => [...list, {
            id: nextId++,
            title: n.title || 'New notification',
            body: n.message || n.body || null,
            to: tripId ? `/rides/${tripId}` : '/notifications',
        }])
    }), [queryClient])

    if (toasts.length === 0) return null

    return (
        <div className="pointer-events-none fixed top-4 right-4 left-4 z-[999] flex flex-col items-center gap-2 md:left-auto md:w-[400px] md:items-end">
            {toasts.map((t) => <Toast key={t.id} toast={t} onDismiss={dismiss}/>)}
        </div>
    )
}
